/**
 * Data sanitization utilities for logging.
 * Removes or masks sensitive information before persisting logs.
 */

/** Keys that should be completely redacted (value replaced with '[REDACTED]') */
const REDACT_KEYS = new Set([
  'accesstoken',
  'refreshtoken',
  'password',
  'secret',
  'apikey',
  'api_key',
  'token',
  'credential',
  'credentials',
  'privatekey',
  'private_key',
])

/** Keys whose values should be partially masked */
const MASK_KEYS: ReadonlySet<string> = new Set(['authorization', 'email', 'fullname', 'full_name', 'name'])

/** Maximum depth for recursive sanitization to prevent stack overflow */
const MAX_DEPTH = 10

/** Maximum string length before truncation */
const MAX_STRING_LENGTH = 1000

/**
 * Masks an email address, preserving domain and partial local part.
 * Example: "john.doe@example.com" -> "j***e@example.com"
 */
function maskEmail(email: string): string {
  const atIndex = email.indexOf('@')
  if (atIndex <= 0) {
    return '***@***'
  }

  const localPart = email.slice(0, atIndex)
  const domain = email.slice(atIndex)

  if (localPart.length <= 2) {
    return `${'*'.repeat(localPart.length)}${domain}`
  }

  return `${localPart[0]}***${localPart[localPart.length - 1]}${domain}`
}

/**
 * Masks a name, showing only first initial.
 * Example: "John Doe" -> "J***"
 */
function maskName(name: string): string {
  if (name.length === 0) {
    return '***'
  }
  return `${name[0]}***`
}

/**
 * Masks an authorization header value.
 * Example: "Bearer eyJ..." -> "Bearer ***"
 */
function maskAuthHeader(value: string): string {
  const parts = value.split(' ')
  if (parts.length >= 2) {
    return `${parts[0]} ***`
  }
  return '***'
}

/**
 * Truncates a string if it exceeds the maximum length.
 */
function truncateString(value: string): string {
  if (value.length <= MAX_STRING_LENGTH) {
    return value
  }
  return `${value.slice(0, MAX_STRING_LENGTH)}... [truncated, ${value.length} chars total]`
}

/**
 * Checks if a value looks like an email address.
 */
function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

/**
 * Sanitizes a single value based on its key.
 */
function sanitizeValue(key: string, value: unknown, depth: number): unknown {
  const lowerKey = key.toLowerCase()

  // Check for complete redaction
  if (REDACT_KEYS.has(lowerKey)) {
    return '[REDACTED]'
  }

  // Handle string values
  if (typeof value === 'string') {
    // Check for masking based on key using MASK_KEYS set
    if (MASK_KEYS.has(lowerKey)) {
      if (lowerKey === 'authorization') {
        return maskAuthHeader(value)
      }
      if (lowerKey === 'email') {
        return maskEmail(value)
      }
      if (lowerKey === 'fullname' || lowerKey === 'full_name') {
        return maskName(value)
      }
      // 'name' key with space indicates full name
      if (lowerKey === 'name' && value.includes(' ')) {
        return maskName(value)
      }
    }

    // Check for email pattern regardless of key
    if (isEmail(value)) {
      return maskEmail(value)
    }

    // Truncate long strings
    return truncateString(value)
  }

  // Recursively sanitize objects and arrays
  if (value !== null && typeof value === 'object') {
    return sanitizeObject(value, depth + 1)
  }

  return value
}

/**
 * Recursively sanitizes an object, removing or masking sensitive data.
 */
function sanitizeObject(data: unknown, depth = 0): unknown {
  // Prevent infinite recursion
  if (depth > MAX_DEPTH) {
    return '[MAX_DEPTH_EXCEEDED]'
  }

  // Handle null/undefined
  if (data === null || data === undefined) {
    return data
  }

  // Handle primitive types
  if (typeof data !== 'object') {
    if (typeof data === 'string') {
      return truncateString(data)
    }
    return data
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item, index) => sanitizeValue(String(index), item, depth))
  }

  // Handle objects
  const sanitized: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    sanitized[key] = sanitizeValue(key, value, depth)
  }

  return sanitized
}

/**
 * Sanitizes data for logging, removing or masking sensitive information.
 *
 * - Completely redacts: accessToken, refreshToken, password, secret, apiKey, token, credentials, privateKey
 * - Masks: Authorization headers (-> "Bearer ***"), email addresses, full names
 * - Truncates: Long strings (> 1000 chars)
 * - Handles: Nested objects and arrays up to 10 levels deep
 *
 * @param data - The data to sanitize
 * @returns A deep copy with sensitive data removed or masked
 *
 * @example
 * ```typescript
 * sanitizeLogData({
 *   user: { email: 'john@example.com', password: 'secret123' },
 *   headers: { Authorization: 'Bearer eyJhbG...' }
 * })
 * // Returns:
 * // {
 * //   user: { email: 'j***n@example.com', password: '[REDACTED]' },
 * //   headers: { Authorization: 'Bearer ***' }
 * // }
 * ```
 */
export function sanitizeLogData<T = unknown>(data: T): T {
  if (data === null || data === undefined) {
    return data
  }

  // Handle Error objects specially
  if (data instanceof Error) {
    return {
      name: data.name,
      message: truncateString(data.message),
      stack: data.stack ? truncateString(data.stack) : undefined,
    } as T
  }

  return sanitizeObject(data) as T
}

/**
 * Creates a sanitized copy of HTTP headers.
 * Convenience function for sanitizing request/response headers.
 */
export function sanitizeHeaders(
  headers: Record<string, string | string[] | undefined> | undefined
): Record<string, string | string[] | undefined> | undefined {
  if (!headers) {
    return undefined
  }

  return sanitizeLogData(headers)
}

/**
 * Extracts and sanitizes relevant error information.
 */
export function sanitizeError(error: unknown): Record<string, unknown> {
  if (error === null || error === undefined) {
    return { message: 'Unknown error' }
  }

  if (error instanceof Error) {
    return {
      name: error.name,
      message: truncateString(error.message),
      stack: error.stack ? truncateString(error.stack) : undefined,
    }
  }

  if (typeof error === 'object') {
    return sanitizeLogData(error as Record<string, unknown>)
  }

  return { message: String(error) }
}
